import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { api } from '../api/client';
import { apiErrorText } from '../api/errorText';
import type { PendingPluginChange, PluginDescriptor } from '../api/types';
import { useApi } from '../api/useApi';
import AsyncSection from '../components/AsyncSection';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../auth/AuthContext';
import { pluginKey } from '../i18n';

const stateColor = {
  Loaded: 'success',
  Disabled: 'default',
  Failed: 'error',
} as const;

/// What the confirmation dialog is currently asking about.
type Confirming = { kind: 'remove'; plugin: PluginDescriptor } | { kind: 'reload' };

/// Everything the plugin host loaded, including the ones that failed and the reason why, and
/// whether each shipped with the server.
///
/// External plugins are installed, updated and removed from here. None of that happens under a
/// running server: an upload is checked and set aside, and it is made when the plugins are
/// reloaded - which is the server starting over in place, and which this page asks before doing.
export default function PluginsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayConfigure = can('ManagePlugins');
  const mayManage = can('ManagePlugins') && can('ManageServerSettings');

  const plugins = useApi(['plugins'], api.plugins);
  const pending = useApi(['plugins', 'pending'], api.pendingPluginChanges, { enabled: mayConfigure });

  const [confirming, setConfirming] = useState<Confirming>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<{ message: string; remedy?: string }>();
  const [reloading, setReloading] = useState<'running' | 'failed'>();

  /// The file chooser, and which plugin the next chosen file updates - none for a new one.
  const fileInput = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<PluginDescriptor>();
  const [uploading, setUploading] = useState(false);

  const nameOf = (plugin: PluginDescriptor) =>
    t(pluginKey(plugin.manifest.id, 'plugin.name'), { defaultValue: plugin.manifest.name });

  const changes = pending.data ?? [];

  const choose = (plugin?: PluginDescriptor) => {
    setReplacing(plugin);
    setProblem(undefined);
    fileInput.current?.click();
  };

  const upload = async (file: File) => {
    setUploading(true);
    setProblem(undefined);

    try {
      await api.uploadPlugin(file, replacing?.manifest.id);
      pending.reload();
    } catch (caught) {
      setProblem(apiErrorText(caught, t));
    } finally {
      setUploading(false);
      setReplacing(undefined);
    }
  };

  const discard = async (change: PendingPluginChange) => {
    try {
      await api.discardPluginChange(change.folder);
      pending.reload();
    } catch (caught) {
      setProblem(apiErrorText(caught, t));
    }
  };

  /// Starts the server over and waits for it to answer again, then loads the page afresh: the
  /// navigation, the plugin translations and everything else a plugin contributes came from the
  /// server that just went away.
  const reload = async () => {
    await api.reloadPlugins();
    setReloading('running');

    // The server answers first and stops a moment later; asking straight away would find the
    // old one still there.
    await new Promise((resolve) => setTimeout(resolve, 2500));

    for (let attempt = 0; attempt < 60; attempt++) {
      try {
        const health = await fetch('/health', { cache: 'no-store' });

        if (health.ok) {
          window.location.reload();

          return;
        }
      } catch {
        // Not back yet.
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setReloading('failed');
  };

  const confirm = async () => {
    if (!confirming) {
      return;
    }

    setBusy(true);
    setProblem(undefined);

    try {
      if (confirming.kind === 'reload') {
        setConfirming(undefined);
        await reload();

        return;
      }

      await api.removePlugin(confirming.plugin.manifest.id);

      setConfirming(undefined);
      plugins.reload();
      pending.reload();
    } catch (caught) {
      setProblem(apiErrorText(caught, t));
    } finally {
      setBusy(false);
    }
  };

  const megabytes = (bytes: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 }).format(
      bytes / 1024 / 1024,
    );

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t('plugins.title')}
        actions={
          mayManage && (
            <Button
              variant="outlined"
              startIcon={uploading && !replacing ? <CircularProgress size={18} /> : <UploadFileIcon />}
              disabled={uploading || reloading !== undefined}
              onClick={() => choose()}
            >
              {t('plugins.upload.action')}
            </Button>
          )
        }
      />

      <input
          ref={fileInput}
          type="file"
          accept=".zip,.dll"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];

            // Emptied at once, so choosing the same file again is still a change.
            event.target.value = '';

            if (file) {
              void upload(file);
            }
          }}
        />

      {reloading === 'running' && (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          {t('plugins.reload.running')}
        </Alert>
      )}

      {reloading === 'failed' && <Alert severity="error">{t('plugins.reload.failed')}</Alert>}

      {problem && !confirming && (
        <Alert severity="error" onClose={() => setProblem(undefined)}>
          <div>{problem.message}</div>
          {problem.remedy && <div>{problem.remedy}</div>}
        </Alert>
      )}

      {/* What waits for the next load, with the one button that makes it happen. A change that
          sits there unannounced is how somebody ends up wondering why the update did nothing. */}
      {changes.length > 0 && mayConfigure && reloading === undefined && (
        <Alert
          severity="warning"
          action={
            mayManage ? (
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={() => setConfirming({ kind: 'reload' })}>
                {t('plugins.reload.action')}
              </Button>
            ) : undefined
          }
        >
          <AlertTitle>{t('plugins.pending.title')}</AlertTitle>
          <Stack spacing={0.5}>
            {changes.map((change) => (
              <Stack key={change.folder} direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  {t(`plugins.pending.${change.kind}`, {
                    name: change.assemblyName,
                    size: megabytes(change.sizeInBytes),
                  })}
                </Typography>
                {mayManage && (
                  <Button size="small" color="inherit" sx={{ py: 0 }} onClick={() => void discard(change)}>
                    {t('plugins.pending.discard')}
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}

      <AsyncSection loading={plugins.loading} error={plugins.error} onRetry={plugins.reload}>
        <Stack spacing={2}>
          {plugins.data?.map((plugin) => {
            const external = plugin.trust === 'External';

            return (
              <Card key={plugin.manifest.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {nameOf(plugin)}
                    </Typography>
                    <Chip size="small" color={stateColor[plugin.state]} label={t(`plugins.state.${plugin.state}`)} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(external ? 'plugins.external' : 'plugins.builtIn')}
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {plugin.manifest.version} · {kindsLabel(plugin.manifest.kinds, t)}
                  </Typography>

                  {plugin.manifest.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t(pluginKey(plugin.manifest.id, 'plugin.description'), {
                        defaultValue: plugin.manifest.description,
                      })}
                    </Typography>
                  )}

                  {plugin.error && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {plugin.error}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, flexWrap: 'wrap' }}>
                    {plugin.hasConfiguration && plugin.state === 'Loaded' && mayConfigure && (
                      <Button
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => navigate(`/plugins/${encodeURIComponent(plugin.manifest.id)}`)}
                      >
                        {t('plugins.configure')}
                      </Button>
                    )}

                    {external && mayManage && (
                      <>
                        <Button
                          size="small"
                          startIcon={
                            uploading && replacing?.manifest.id === plugin.manifest.id
                              ? <CircularProgress size={16} />
                              : <SystemUpdateAltIcon />
                          }
                          disabled={uploading || reloading !== undefined}
                          onClick={() => choose(plugin)}
                        >
                          {t('plugins.update')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          disabled={reloading !== undefined}
                          onClick={() => setConfirming({ kind: 'remove', plugin })}
                        >
                          {t('plugins.remove.action')}
                        </Button>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </AsyncSection>

      <Dialog open={confirming !== undefined} onClose={() => !busy && setConfirming(undefined)} fullWidth maxWidth="sm">
        {confirming && (
          <>
            <DialogTitle>
              {confirming.kind === 'reload'
                ? t('plugins.reload.title')
                : t('plugins.remove.title', { name: nameOf(confirming.plugin) })}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography>{t(confirming.kind === 'reload' ? 'plugins.reload.body' : 'plugins.remove.body')}</Typography>
                {problem && (
                  <Alert severity="error">
                    <div>{problem.message}</div>
                    {problem.remedy && <div>{problem.remedy}</div>}
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button disabled={busy} onClick={() => setConfirming(undefined)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color={confirming.kind === 'remove' ? 'error' : 'primary'}
                disabled={busy}
                onClick={() => void confirm()}
              >
                {t(confirming.kind === 'reload' ? 'plugins.reload.action' : 'plugins.remove.action')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}

/// What a plugin contributes, in words. The server sends the flags as it names them.
function kindsLabel(kinds: string, t: (key: string, options?: { defaultValue: string }) => string): string {
  return kinds
    .split(',')
    .map((kind) => kind.trim())
    .filter((kind) => kind.length > 0 && kind !== 'None')
    .map((kind) => t(`plugins.kind.${kind}`, { defaultValue: kind }))
    .join(', ');
}

